import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBolt, faList, faUsers, faVideo, faComments } from '@/fontawesome';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import { APIRoutes } from '@/services/api.routes';
import { useDayTradingWebSocket, streamInfoFromSession } from '@/hooks/useDayTradingWebSocket';
import { toUSDCentValue } from '@/utils/helpers';
import { showErrorToast, handleAPIError } from '@/utils/toasts';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConnectionStatusIndicator from '@/components/daytrading/components/ConnectionStatusIndicator';
import CardSizeSlider from '@/components/daytrading/components/CardSizeSlider';
import ModeratorTradeCard from '@/components/daytrading/components/ModeratorTradeCard.tsx';
import ParticipantsList from '@/components/daytrading/components/ParticipantsList.tsx';
import HistoryPanel from '@/components/daytrading/components/HistoryPanel';
import ChatPanel from '@/components/daytrading/components/ChatPanel';
import StreamPiP from '@/components/daytrading/components/StreamPiP';
import StreamHeaderButton from '@/components/daytrading/components/StreamHeaderButton';
import SoundMuteButton from '@/components/daytrading/components/SoundMuteButton';
import Modal from '@/components/ui/Modal';
import NumberInput from '@/components/forms/NumberInput';
import { useSoundMuted } from '@/hooks/useSoundMuted';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { useUser } from '@/contexts/UserContext';
import { cleanYouTubeUrl, isValidYouTubeUrl } from '@/utils/youtube-url';
import { isValidGoogleMeetUrl } from '@/utils/google-meet-url';
import { isValidDiscordUrl, DEFAULT_DISCORD_URL } from '@/utils/discord-url';
import StreamURLInput from '@/components/daytrading/components/StreamURLInput';
import { useStreamStatus } from '@/hooks/useStreamStatus';
import { ALL_STREAM_MODES, STREAM_MODE_LABELS } from '@/models/enums';
import type { StreamMode } from '@/models/enums';
import type { DayTradingSessionID, DayTradeAdjustementID, DayTradeID } from '@/models/ids';
import type { DayTradeType, DayTradeStockCountMode } from '@/models/enums';
import type { Percentage, USDCent } from '@/models/base-types';
import type { DayTradingSessionState, DayTradingSessionChat } from '@/models/day-trading-session';
import type { UserID } from '@/models/ids';
import type { DayTradingParticipantState } from '@/models/day-trading-participant';

const SHARE_PRESETS: { label: string; value: Percentage }[] = [
  { label: '1/2', value: 0.5 },
  { label: '1/3', value: 1 / 3 },
  { label: '1/4', value: 0.25 },
  { label: '1/5', value: 0.2 },
  { label: '1/6', value: 1 / 6 },
  { label: '1/8', value: 0.125 },
  { label: '1/10', value: 0.1 },
  { label: '1/20', value: 0.05 },
];

export default function DayTradingModeratorPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const dtsid = sessionId as DayTradingSessionID;

  const {
    initialLoading,
    initialError,
    connectionStatus,
    error,
    sessionDeleted,

    sessionState,
    setSessionState,

    sessionClosed,
    setSessionClosed,

    participantState,
    setParticipantState,

    participants,
    moderators,
    moderatorPings,
    moderatorPolls,

    streamInfo,
    setStreamInfo,

    chat,
    setChat,
  } = useDayTradingWebSocket({
    sessionId: dtsid,
    role: 'moderator',
  });

  const { user, isEmployee, isAdmin } = useUser();
  const { isMuted, toggleMuted } = useSoundMuted();
  const { play } = useNotificationSound(isMuted);

  const streamStatus = useStreamStatus(dtsid, streamInfo, !sessionClosed);

  // Create trade form state
  const [symbol, setSymbol] = useState('');
  const [tradeType, setTradeType] = useState<DayTradeType | null>(null);
  const [shareMode, setShareMode] = useState<'preset' | 'custom'>('preset');
  const [sharePreset, setSharePreset] = useState(0.1);
  const [customShare, setCustomShare] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [targetPrice, setTargetPrice] = useState('');

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showStreamEdit, setShowStreamEdit] = useState(false);
  const [streamUrlInput, setStreamUrlInput] = useState('');
  const [googleMeetUrlInput, setGoogleMeetUrlInput] = useState('');
  const [discordUrlInput, setDiscordUrlInput] = useState('');
  const [streamUrlSaving, setStreamUrlSaving] = useState(false);
  const [livestreamMinimized, setLivestreamMinimized] = useState(true);
  const [streamModeInput, setStreamModeInput] = useState<StreamMode>('NONE');
  const [regeneratingStreamKey, setRegeneratingStreamKey] = useState(false);

  // Budget state (for moderator-as-participant)
  const [budgetInput, setBudgetInput] = useState<string>('');
  const [cardScale, setCardScale] = useState(1.0);
  const [historyMinimized, setHistoryMinimized] = useState(false);
  const [participantsMinimized, setParticipantsMinimized] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  const currentUserId = (user?.id ?? '') as UserID;
  const chatLastViewedId = chat?.lastViewed[currentUserId]?.messageID ?? null;
  const chatLatest = chat !== null && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
  const chatHasUnread =
    chatLatest !== null && chatLatest.id !== chatLastViewedId && chatLatest.userID !== currentUserId;

  const handleChatUpdate = useCallback(
    (next: DayTradingSessionChat) => setChat(next),
    [setChat],
  );

  const handleOpenStandaloneChat = useCallback(() => {
    window.open(
      `${DAY_TRADING_ROUTES.CHAT(dtsid)}?role=moderator`,
      '_blank',
      'width=500,height=800,resizable=yes,scrollbars=yes',
    );
    setChatMinimized(true);
  }, [dtsid]);
  const lastServerBudgetRef = useRef<number | null>(null);

  useEffect(() => {
    if (participantState !== null && participantState.budget !== lastServerBudgetRef.current && budgetDebounceRef.current === null) {
      lastServerBudgetRef.current = participantState.budget;
      setBudgetInput(Math.round(participantState.budget / 100).toString());
    }
  }, [participantState]);

  const budgetCents: USDCent = Math.round(parseFloat(budgetInput || '0') * 100);

  const updateParticipantState = useCallback(async (state: DayTradingParticipantState) => {
    try {
      const result = await APIRoutes.updateDayTradingParticipantState(dtsid, 'self', { state });
      setParticipantState(result.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren des Status');
    }
  }, [dtsid, setParticipantState]);

  const budgetDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBudgetChange = useCallback((value: string) => {
    setBudgetInput(value);

    if (participantState !== null) {
      const newBudgetCents = Math.round(parseFloat(value || '0') * 100);
      lastServerBudgetRef.current = newBudgetCents;

      if (budgetDebounceRef.current !== null) {
        clearTimeout(budgetDebounceRef.current);
      }

      budgetDebounceRef.current = setTimeout(() => {
        budgetDebounceRef.current = null;
        const newState: DayTradingParticipantState = {
          ...participantState,
          budget: newBudgetCents,
          lastChange: new Date().toISOString(),
        };
        updateParticipantState(newState);
      }, 500);
    }
  }, [participantState, updateParticipantState]);

  useEffect(() => {
    return () => {
      if (budgetDebounceRef.current !== null) {
        clearTimeout(budgetDebounceRef.current);
      }
    };
  }, []);

  // Auto-apply stockCountMode from last trade to new trades
  useEffect(() => {
    if (participantState === null || sessionState === null) return;
    const trades = sessionState.trades ?? [];
    if (trades.length < 2) return;

    const modes = participantState.stockCountMode ?? {};
    const newTrades = trades.filter((t) => modes[t.id] === undefined);
    if (newTrades.length === 0) return;

    const existingTrades = trades.filter((t) => modes[t.id] !== undefined);
    if (existingTrades.length === 0) return;

    const lastMode = modes[existingTrades[existingTrades.length - 1].id];
    if (lastMode === 'NORMAL') return;

    const updatedModes = { ...modes };
    for (const t of newTrades) {
      updatedModes[t.id] = lastMode;
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      stockCountMode: updatedModes,
      lastChange: new Date().toISOString(),
    };
    updateParticipantState(newState);
  }, [sessionState, participantState, updateParticipantState]);

  const handleOverrideAdjustmentStockCount = useCallback((adjId: DayTradeAdjustementID, count: number | null) => {
    if (participantState === null) return;

    const overrides = { ...(participantState.overrideAdjustmentStockCount ?? {}) };
    if (count === null) {
      delete overrides[adjId];
    } else {
      overrides[adjId] = count;
    }

    const newState: DayTradingParticipantState = {
      ...participantState,
      overrideAdjustmentStockCount: overrides,
      lastChange: new Date().toISOString(),
    };
    updateParticipantState(newState);
  }, [participantState, updateParticipantState]);

  const handleToggleStockCountMode = useCallback((tradeId: DayTradeID) => {
    if (participantState === null) return;

    const modes = { ...(participantState.stockCountMode ?? {}) };
    const current: DayTradeStockCountMode = modes[tradeId] ?? 'NORMAL';
    modes[tradeId] = current === 'NORMAL' ? 'FIRST' : 'NORMAL';

    const newState: DayTradingParticipantState = {
      ...participantState,
      stockCountMode: modes,
      lastChange: new Date().toISOString(),
    };
    updateParticipantState(newState);
  }, [participantState, updateParticipantState]);

  const handleCreateTrade = useCallback(async () => {
    const share = shareMode === 'custom' ? parseFloat(customShare) : sharePreset;
    const entry = parseFloat(entryPrice.replace(',', '.'));
    const target = parseFloat(targetPrice.replace(',', '.'));

    if (symbol.trim() === '') { showErrorToast('Bitte ein Symbol eingeben'); return; }
    if (tradeType === null) { showErrorToast('Bitte eine Richtung wählen (Long/Short)'); return; }
    if (isNaN(share) || isNaN(entry) || isNaN(target)) { showErrorToast('Bitte alle Felder korrekt ausfüllen'); return; }
    if (share <= 0 || share > 1) { showErrorToast('Anteil muss zwischen 0 und 1 liegen'); return; }
    if (entry <= 0 || target <= 0) { showErrorToast('Preise müssen größer als 0 sein'); return; }

    try {
      const response = await APIRoutes.createDayTrade(dtsid, {
        symbol: symbol.trim().toUpperCase(),
        type: tradeType,
        share,
        entryPrice: toUSDCentValue(entry),
        targetPrice: toUSDCentValue(target),
      });

      setSessionState(response.state);

      setSymbol('');
      setTradeType(null);
      setEntryPrice('');
      setTargetPrice('');
    } catch (err) {
      handleAPIError(err, 'Fehler beim Erstellen des Trades');
    }
  }, [symbol, tradeType, shareMode, sharePreset, customShare, entryPrice, targetPrice, dtsid, setSessionState]);

  const handleCloseSession = useCallback(async () => {
    try {
      const session = await APIRoutes.closeDayTradingSession(dtsid);
      setSessionState(session.state);
      setSessionClosed(true);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Beenden der Session');
    }
    setShowCloseConfirm(false);
  }, [dtsid, setSessionState, setSessionClosed]);

  const handleStateUpdated = useCallback((newState: DayTradingSessionState) => {
    setSessionState(newState);
  }, [setSessionState]);

  const handleOpenStreamEdit = useCallback(() => {
    setStreamModeInput(streamInfo.mode);
    setStreamUrlInput(streamInfo.youtubeURL);
    setGoogleMeetUrlInput(streamInfo.googleMeetURL);
    setDiscordUrlInput(streamInfo.discordURL === '' ? DEFAULT_DISCORD_URL : streamInfo.discordURL);
    setShowStreamEdit(true);
  }, [streamInfo.mode, streamInfo.youtubeURL, streamInfo.googleMeetURL, streamInfo.discordURL]);

  const handleSaveStream = useCallback(async () => {
    // YouTube, Google Meet and Discord all use soft validation: send the
    // canonical form when we can recognize it, otherwise fall back to the
    // raw input.
    const cleanedYoutubeURL = streamModeInput === 'YOUTUBE'
      ? (cleanYouTubeUrl(streamUrlInput) ?? streamUrlInput.trim())
      : '';
    const trimmedGoogleMeetURL = streamModeInput === 'GOOGLE_MEET' ? googleMeetUrlInput.trim() : '';
    const trimmedDiscordURL = streamModeInput === 'DISCORD' ? discordUrlInput.trim() : '';
    setStreamUrlSaving(true);
    try {
      let session = streamInfo.mode === streamModeInput
        ? null
        : await APIRoutes.updateDayTradingSession(dtsid, { streamMode: streamModeInput });
      if (streamModeInput === 'YOUTUBE' && cleanedYoutubeURL !== streamInfo.youtubeURL) {
        session = await APIRoutes.updateDayTradingSession(dtsid, { youtubeURL: cleanedYoutubeURL });
      }
      if (streamModeInput === 'GOOGLE_MEET' && trimmedGoogleMeetURL !== streamInfo.googleMeetURL) {
        session = await APIRoutes.updateDayTradingSession(dtsid, { googleMeetURL: trimmedGoogleMeetURL });
      }
      if (streamModeInput === 'DISCORD' && trimmedDiscordURL !== streamInfo.discordURL) {
        session = await APIRoutes.updateDayTradingSession(dtsid, { discordURL: trimmedDiscordURL });
      }
      if (session !== null) {
        setStreamInfo(streamInfoFromSession(session));
      }
      setShowStreamEdit(false);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren des Streams');
    } finally {
      setStreamUrlSaving(false);
    }
  }, [dtsid, streamModeInput, streamUrlInput, googleMeetUrlInput, discordUrlInput, streamInfo.mode, streamInfo.youtubeURL, streamInfo.googleMeetURL, streamInfo.discordURL, setStreamInfo]);

  const handleRegenerateStreamKey = useCallback(async () => {
    setRegeneratingStreamKey(true);
    try {
      const session = await APIRoutes.regenerateDayTradingSessionStreamKey(dtsid);
      setStreamInfo(streamInfoFromSession(session));
    } catch (err) {
      handleAPIError(err, 'Fehler beim Erzeugen eines neuen Stream-Keys');
    } finally {
      setRegeneratingStreamKey(false);
    }
  }, [dtsid, setStreamInfo]);

  const trades = sessionState?.trades ?? [];

  if (initialLoading) {
    return <LoadingSpinner message="Lade Session..." />;
  }

  if (initialError !== null) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          {initialError}
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (sessionDeleted) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          Diese Session wurde gelöscht.
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      <div className="mb-6">
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faBolt} className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Daytrading Session: Moderation
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <CardSizeSlider scale={cardScale} onScaleChange={setCardScale} />
          <SoundMuteButton isMuted={isMuted} onToggle={toggleMuted} play={play} />
          <ConnectionStatusIndicator status={connectionStatus} />
          {!sessionClosed && (
            <button
              onClick={() => setShowCloseConfirm(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
            >
              Session beenden
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error !== null && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Session closed banner */}
      {sessionClosed && (
        <div className="mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200">
          Diese Session wurde beendet.
        </div>
      )}

      {/* Budget + Stream controls bar */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-5 py-3 flex items-center gap-3">
        {participantState !== null && (
          <>
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
              Depotgröße
            </label>
            <NumberInput
              value={budgetInput}
              onChange={handleBudgetChange}
              placeholder="10000"
              suffix="$"
              ariaLabel="Depotgröße"
            />
          </>
        )}
        <div className="flex-1" />
        {(historyMinimized || participantsMinimized || chatMinimized) && (
          <>
            {chatMinimized && (
              <button
                onClick={() => setChatMinimized(false)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border cursor-pointer transition-colors ${
                  chatHasUnread
                    ? 'border-blue-500 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 animate-pulse'
                    : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={chatHasUnread ? 'Neue Nachrichten' : 'Chat öffnen'}
              >
                <FontAwesomeIcon icon={faComments} className="w-4 h-4" />
                Chat
                {chatHasUnread && (
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                )}
              </button>
            )}
            {participantsMinimized && (
              <button
                onClick={() => setParticipantsMinimized(false)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <FontAwesomeIcon icon={faUsers} className="w-4 h-4" />
                Teilnehmer
              </button>
            )}
            {historyMinimized && (
              <button
                onClick={() => setHistoryMinimized(false)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              >
                <FontAwesomeIcon icon={faList} className="w-4 h-4" />
                Historie
              </button>
            )}
          </>
        )}
        <StreamHeaderButton
          streamInfo={streamInfo}
          sessionClosed={sessionClosed}
          variant="moderator"
          status={streamStatus}
          onOpenPiP={() => setLivestreamMinimized(false)}
          onEdit={handleOpenStreamEdit}
        />
      </div>

      {/* Create Trade Form */}
      {!sessionClosed && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Trade hinzufügen</h2>
          <div className="flex flex-wrap items-end gap-3">
            {/* Symbol */}
            <div className="flex-1 min-w-[100px]">
              <label className="block text-[0.7rem] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Symbol</label>
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="AAPL"
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white uppercase"
              />
            </div>

            {/* Direction toggle */}
            <div className="min-w-[70px] sm:min-w-[130px]">
              <label className="block text-[0.7rem] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Richtung</label>
              <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                <button
                  onClick={() => setTradeType('LONG')}
                  title="Long"
                  className={`flex-1 px-2 sm:px-3 py-2 text-sm font-bold cursor-pointer transition-colors whitespace-nowrap ${
                    tradeType === 'LONG'
                      ? 'bg-green-900/50 text-green-500'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  ▲<span className="hidden sm:inline"> Long</span>
                </button>
                <button
                  onClick={() => setTradeType('SHORT')}
                  title="Short"
                  className={`flex-1 px-2 sm:px-3 py-2 text-sm font-bold cursor-pointer transition-colors whitespace-nowrap ${
                    tradeType === 'SHORT'
                      ? 'bg-red-900/50 text-red-500'
                      : 'bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  ▼<span className="hidden sm:inline"> Short</span>
                </button>
              </div>
            </div>

            {/* Share */}
            <div className="flex-1 min-w-[130px]">
              <label className="block text-[0.7rem] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Anteil</label>
              <div className="flex items-center gap-1">
                <select
                  value={shareMode === 'preset' ? sharePreset.toString() : 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setShareMode('custom');
                    } else {
                      setShareMode('preset');
                      setSharePreset(parseFloat(e.target.value));
                    }
                  }}
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
                >
                  {SHARE_PRESETS.map((p) => (
                    <option key={p.label} value={p.value.toString()}>
                      {p.label}
                    </option>
                  ))}
                  <option value="custom">Eigener...</option>
                </select>
                {shareMode === 'custom' && (
                  <input
                    type="number"
                    value={customShare}
                    onChange={(e) => setCustomShare(e.target.value)}
                    placeholder="0.25"
                    step="0.01"
                    min="0.01"
                    max="1"
                    className="w-20 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                )}
              </div>
            </div>

            {/* Entry Price */}
            <div className="flex-1 min-w-[110px]">
              <label className="block text-[0.7rem] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Einstieg $</label>
              <input
                type="text"
                inputMode="decimal"
                value={entryPrice}
                onChange={(e) => setEntryPrice(e.target.value)}
                placeholder="680,01"
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Target Price */}
            <div className="flex-1 min-w-[110px]">
              <label className="block text-[0.7rem] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">Ziel $</label>
              <input
                type="text"
                inputMode="decimal"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                placeholder="7,00"
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleCreateTrade}
              disabled={symbol.trim() === '' || tradeType === null}
              className="px-5 py-2 text-sm font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap"
            >
              + Hinzufügen
            </button>
          </div>
        </div>
      )}

      {/* Content: trades + participants */}
      <div className={!historyMinimized || !participantsMinimized || !chatMinimized ? 'grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6' : ''}>
        {/* Trades grid */}
        {trades.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Noch keine Trades in dieser Session.
          </div>
        ) : (
          <div className="flex flex-wrap gap-5 self-start" style={{ zoom: cardScale }}>
            {trades.map((trade) => (
              <ModeratorTradeCard
                key={trade.id}
                trade={trade}
                sessionId={dtsid}
                disabled={sessionClosed}
                onStateUpdated={handleStateUpdated}
                budget={budgetCents}
                participantState={participantState}
                onOverrideAdjustmentStockCount={handleOverrideAdjustmentStockCount}
                onToggleStockCountMode={() => handleToggleStockCountMode(trade.id)}
                stockCountMode={participantState?.stockCountMode?.[trade.id] ?? 'NORMAL'}
              />
            ))}
          </div>
        )}

        {/* Participants sidebar */}
        {(!historyMinimized || !participantsMinimized || !chatMinimized) && (
          <div className="min-w-80 flex flex-col gap-6">
            {!participantsMinimized && (
              <ParticipantsList participants={participants} moderators={moderators} moderatorPings={moderatorPings} moderatorPolls={moderatorPolls} sessionClosed={sessionClosed} onMinimize={() => setParticipantsMinimized(true)} />
            )}
            {!historyMinimized && (
              <HistoryPanel history={sessionState?.history ?? []} onMinimize={() => setHistoryMinimized(true)} />
            )}
            {!chatMinimized && chat !== null && user !== null && (
              <ChatPanel
                chat={chat}
                sessionId={dtsid}
                currentUserId={currentUserId}
                canWrite={true}
                canDelete={isEmployee || isAdmin}
                isMuted={isMuted}
                onChatUpdate={handleChatUpdate}
                onMinimize={() => setChatMinimized(true)}
                onOpenStandalone={handleOpenStandaloneChat}
              />
            )}
          </div>
        )}
      </div>

      {/* Close session confirmation */}
      <Modal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        title="Session beenden"
        maxWidth="sm"
      >
        <div className="p-6 space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Möchtest du die Session wirklich beenden? Alle Teilnehmer werden benachrichtigt.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowCloseConfirm(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCloseSession}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
            >
              Beenden
            </button>
          </div>
        </div>
      </Modal>

      {/* Stream edit modal */}
      <Modal
        isOpen={showStreamEdit}
        onClose={() => setShowStreamEdit(false)}
        title="Stream bearbeiten"
        icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
        maxWidth="lg"
        closeDisabled={streamUrlSaving || regeneratingStreamKey}
      >
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stream-Modus
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ALL_STREAM_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setStreamModeInput(mode);
                    if (mode === 'DISCORD' && discordUrlInput.trim() === '') {
                      setDiscordUrlInput(DEFAULT_DISCORD_URL);
                    }
                  }}
                  disabled={streamUrlSaving}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer ${
                    streamModeInput === mode
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {STREAM_MODE_LABELS[mode]}
                </button>
              ))}
            </div>
          </div>

          {streamModeInput === 'YOUTUBE' && (
            <StreamURLInput
              label="YouTube URL"
              placeholder="https://www.youtube.com/watch?v=..."
              value={streamUrlInput}
              onChange={setStreamUrlInput}
              disabled={streamUrlSaving}
              warning={isValidYouTubeUrl(streamUrlInput) ? null : 'Warnung: URL wird nicht als YouTube-Link erkannt.'}
            />
          )}

          {streamModeInput === 'GOOGLE_MEET' && (
            <StreamURLInput
              label="Google Meet URL"
              placeholder="https://meet.google.com/abc-defg-hij"
              value={googleMeetUrlInput}
              onChange={setGoogleMeetUrlInput}
              disabled={streamUrlSaving}
              warning={isValidGoogleMeetUrl(googleMeetUrlInput) ? null : 'Warnung: URL sollte mit „https://meet.google.com/" beginnen.'}
            />
          )}

          {streamModeInput === 'DISCORD' && (
            <StreamURLInput
              label="Discord URL"
              placeholder="https://discord.com/channels/..."
              value={discordUrlInput}
              onChange={setDiscordUrlInput}
              disabled={streamUrlSaving}
              warning={isValidDiscordUrl(discordUrlInput) ? null : 'Warnung: URL sollte mit „https://discord.com/channels/" beginnen.'}
            />
          )}

          {streamModeInput === 'DIRECT' && streamInfo.directStreamIngestURL !== '' && (
            <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">OBS Server (URL)</label>
                <input
                  type="text"
                  readOnly
                  value={streamInfo.directStreamIngestURL}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-text"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">OBS Stream-Key</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={streamInfo.directStreamKey}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                    className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-text"
                  />
                  <button
                    onClick={handleRegenerateStreamKey}
                    disabled={regeneratingStreamKey || streamUrlSaving}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors disabled:opacity-50"
                    title="Neuen Stream-Key generieren (alter Key wird sofort ungültig)"
                  >
                    {regeneratingStreamKey ? 'Erzeuge…' : 'Neu erzeugen'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Viewer-URL (HLS)</label>
                <input
                  type="text"
                  readOnly
                  value={streamInfo.directStreamViewerURL}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  className="w-full px-3 py-2 text-xs font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-text"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowStreamEdit(false)}
              disabled={streamUrlSaving}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSaveStream}
              disabled={streamUrlSaving}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-50"
            >
              Speichern
            </button>
          </div>
        </div>
      </Modal>

      <StreamPiP
        streamInfo={streamInfo}
        minimized={livestreamMinimized}
        onMinimize={() => setLivestreamMinimized(true)}
      />
    </div>
  );
}
